import { delay, put, retry, select } from 'redux-saga/effects'
import { api } from 'lib/api'
import { createTimeoutError } from 'lib/saga-helpers'

const DEFAULT_POLL_INTERVAL_MS = 1000
const DEFAULT_TIMEOUT_MS = 60 * 1000
const DEFAULT_MODEL = 'claude-haiku-4-5-20251001'
const POLL_429_BACKOFF_MS = 2000

const TERMINAL_STATUSES = new Set(['SUCCESS', 'FAILURE', 'REVOKED'])

const extractResponseText = task => {
  const turns = task?.result?.turns || []
  if (turns.length === 0) return null
  const last = turns[turns.length - 1]
  return last.response ?? last.content ?? null
}

/**
 * Run a single-turn ad-hoc assistant prompt: POST /assistant/poll, then poll
 * GET /assistant/tasks/{id} until terminal or cancelled.
 *
 * Returns one of:
 *   { status: 'SUCCESS', responseText, task }
 *   { status: 'FAILURE', error, task }
 *   { status: 'REVOKED', task }
 *   { status: 'CANCELLED' }                 // isCancelled selector was truthy
 *
 * Throws:
 *   - TimeoutError (from createTimeoutError) when timeoutMs is exceeded.
 *   - Re-throws axios errors from the initial POST so callers can switch on
 *     response.status (e.g. 426 → upgrade modal, 429 → rate-limit message).
 *
 * @param {string} prompt              - User input sent to the assistant.
 * @param {function} [isCancelled]     - Redux selector. Polled after each tick;
 *   when it returns truthy the generator returns { status: 'CANCELLED' }.
 * @param {function} [progressAction]  - Called with the latest task on every
 *   non-terminal poll. Should return an action object (or null/undefined to
 *   skip dispatch). Used by callers that surface streaming progress.
 * @param {number} [timeoutMs]
 * @param {number} [pollIntervalMs]
 * @param {string} [model]
 */
export function * runAdHocAssistantPrompt ({
  prompt,
  isCancelled,
  progressAction,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  model = DEFAULT_MODEL
}) {
  const { data } = yield retry(2, 3000, api.post, '/assistant/poll', {
    turns: [],
    user_input: prompt,
    session_id: null,
    neverUseReferences: true,
    numReferences: 0,
    saveSession: false,
    model
  })

  const taskId = data?.id
  if (!taskId) {
    const err = new Error('Assistant poll returned no task id.')
    err.name = 'NoTaskIdError'
    throw err
  }

  const start = Date.now()

  while (true) {
    yield delay(pollIntervalMs)

    let task
    try {
      const { data: pollData } = yield retry(2, 3000, api.get, `/assistant/tasks/${taskId}`)
      task = pollData
    } catch (e) {
      if (e.response?.status === 429) {
        yield delay(POLL_429_BACKOFF_MS)
        continue
      }
      throw e
    }

    const { status } = task

    if (TERMINAL_STATUSES.has(status)) {
      if (status === 'SUCCESS') {
        return { status, responseText: extractResponseText(task), task }
      }
      if (status === 'FAILURE') {
        return { status, error: task.error, task }
      }
      return { status, task } // REVOKED
    }

    if (Date.now() - start > timeoutMs) {
      throw createTimeoutError(task)
    }

    if (isCancelled) {
      const cancelled = yield select(isCancelled)
      if (cancelled) return { status: 'CANCELLED' }
    }

    if (progressAction) {
      const action = progressAction(task)
      if (action && action.type) yield put(action)
    }
  }
}
